import {
  getMaintenanceRequestsWithSettings,
  saveMaintenanceRequest,
  submitMaintenanceFeedback,
  getMaintenanceRequestsWithChatbot,
} from 'portal-modules/maintenance/api/api'
import { addSettings } from 'portal-modules/common/redux/modules/settings'
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import { LOGOUT } from 'portal-modules/auth/redux/modules/user'
import createRequestSaga from 'portal-modules/common/redux/create-request-saga'
import {
  normalizeMaintenanceLocations,
  normalizeMaintenanceCategories,
} from 'portal-modules/common/utils/normalize-maintenance-data'
import createFormAction from 'portal-modules/common/redux/create-form-action'
import { addCustomContent } from 'portal-modules/common/redux/modules/customContent'

// action types
const RECEIVE_MAINTENANCE_REQUESTS =
  'rp/maintenance/RECEIVE_MAINTENANCE_REQUESTS'
const REQUEST_MAINTENANCE_REQUESTS =
  'rp/maintenance/REQUEST_MAINTENANCE_REQUESTS'
const ADD_MAINTENANCE_REQUEST = 'rp/maintenance/ADD_MAINTENANCE_REQUEST'
const MAINTENANCE_REQUEST_ADDED = 'rp/maintenance/MAINTENANCE_REQUEST_ADDED'
const SEND_MAINTENANCE_FEEDBACK = 'rp/maintenance/SEND_MAINTENANCE_FEEDBACK'
const REQUEST_MAINTENANCE_REQUESTS_WITH_CHATBOT =
  'rp/maintenance/REQUEST_MAINTENANCE_REQUESTS_WITH_CHATBOT'
import CompanyLinksApi from 'portal-modules/company-links/api'
import getProjectConditions from 'portal-modules/common/utils/get-project-conditions'
const { isRP } = getProjectConditions()
// reducer
export default (state = {}, { payload, type }) => {
  switch (type) {
    case RECEIVE_MAINTENANCE_REQUESTS:
      return { ...state, ...payload }
    case MAINTENANCE_REQUEST_ADDED:
      return { ...state, [payload.id]: payload }
    case LOGOUT:
      return {}
    default:
      return state
  }
}

// action creators
export const requestMaintenanceRequests = () => ({
  type: REQUEST_MAINTENANCE_REQUESTS,
})

export const requestMaintenanceRequestsWithChatbot = (callback) => ({
  type: REQUEST_MAINTENANCE_REQUESTS_WITH_CHATBOT,
  callback,
})

export const receiveMaintenanceRequests = payload => ({
  type: RECEIVE_MAINTENANCE_REQUESTS,
  payload,
})

export const addMaintenanceRequest = createFormAction(ADD_MAINTENANCE_REQUEST)

export const sendMaintenanceFeedback = (payload, callback) => ({
  type: SEND_MAINTENANCE_FEEDBACK,
  callback,
  payload,
})

// sagas
function* postAddMaintenanceRequest({ payload, type }) {
  const formData = new FormData()
  formData.append(
    'maintenance_request',
    JSON.stringify(payload.maintenance_request),
  )

  payload?.maintenance_image?.map((image, index) => {
    formData.append(`maintenance_image${index}`, image)
  })

  const { maintenance_request } = yield call(saveMaintenanceRequest, formData)

  if (maintenance_request && type === ADD_MAINTENANCE_REQUEST) {
    if (typeof payload.callback == 'function') {
      payload.callback(maintenance_request)
    }
    yield put({
      type: MAINTENANCE_REQUEST_ADDED,
      payload: maintenance_request,
    })

    let { surveyFormData, documentId } = payload
    if (surveyFormData?.length && documentId && maintenance_request.id) {
      CompanyLinksApi.submit({
        document_id: documentId,
        field_data: surveyFormData,
        maintenance_request_id: maintenance_request.id,
      })
    }
  }
}

function* postMaintenanceFeedback({ payload, callback }) {
  yield call(submitMaintenanceFeedback, payload)
  typeof callback == 'function' && callback()
}

function* fetchMaintenanceRequests() {
  const {
    list: { items },
    settings,
  } = yield call(
    getMaintenanceRequestsWithSettings,
    isRP ? { version: 2 } : undefined,
  )

  let actions = []
  if (items) {
    actions.push(receiveMaintenanceRequests(items || {}))
  }

  if (settings) {
    let maintenance = {}
    if (!!settings.use_category_action === true) {
      const { category, location: locations, ...rest } = settings
      const normalized = normalizeMaintenanceCategories(category)
      maintenance = {
        maintenance: {
          locations: locations,
          originalCategory: category,
          originalLocations: locations,
          originalCategory: category,
          ...rest,
          ...normalized,
        },
      }
    } else {
      const { location_problems, ...rest } = settings
      const normalized = normalizeMaintenanceLocations(location_problems)
      maintenance = {
        maintenance: {
          originalLocations: location_problems,
          ...normalized,
          ...rest,
        },
      }
    }
    actions.push(addSettings(maintenance))
    actions.push(addCustomContent({ maintenance: settings.custom_text }))
  }

  yield all(actions.map(a => put(a)))
}

function* fetchMaintenanceRequestsWithChatbot({callback}) {
  const response = yield call(getMaintenanceRequestsWithChatbot)
  yield typeof callback == 'function' && Object.keys(response)?.length && callback(response)
}

function* watchGetMaintenanceRequestsWithChatbot() {
  yield takeLatest(
    REQUEST_MAINTENANCE_REQUESTS_WITH_CHATBOT,
    createRequestSaga(fetchMaintenanceRequestsWithChatbot, {
      key: 'maintenance',
      message: __('Checking for maintenance requests with chatbot.'),
    }),
  )
}

function* watchGetMaintenanceRequests() {
  yield takeLatest(
    REQUEST_MAINTENANCE_REQUESTS,
    createRequestSaga(fetchMaintenanceRequests, {
      key: 'maintenance',
      message: __('Checking for maintenance requests.'),
    }),
  )
}

function* watchAddMaintenanceRequest() {
  yield takeEvery(
    ADD_MAINTENANCE_REQUEST,
    createRequestSaga(postAddMaintenanceRequest, {
      key: 'maintenance',
      message: __('Requesting maintenance'),
      successMessage: __('Request submitted'),
      write: true,
    }),
  )
}

function* watchSendMaintenanceFeedback() {
  yield takeEvery(
    SEND_MAINTENANCE_FEEDBACK,
    createRequestSaga(postMaintenanceFeedback, {
      key: 'maintenanceSurvey',
      message: __('Sending feedback'),
      successMessage: __('Feedback sent successfully'),
      write: true,
    }),
  )
}

export const sagas = [
  watchGetMaintenanceRequests,
  watchGetMaintenanceRequestsWithChatbot,
  watchAddMaintenanceRequest,
  watchSendMaintenanceFeedback,
]
